/**
 * Formatting rules from the approved specification:
 *   NULL  → '—'   (never rendered as 0)
 *   0     → '0 ֏'
 *   never NaN / Infinity / #DIV/0!
 */
const NBSP = '\u00A0';
export const NO_DATA = '—';

export const isMissing = (value: number | null | undefined): value is null | undefined =>
  value === null || value === undefined || !Number.isFinite(value);

export function groupDigits(value: number): string {
  const rounded = Math.round(value);
  const sign = rounded < 0 ? '−' : '';
  return sign + Math.abs(rounded).toString().replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

/** Full precision: 40 479 783 ֏ */
export function formatAmd(value: number | null | undefined): string {
  if (isMissing(value)) return NO_DATA;
  return `${groupDigits(value)}${NBSP}֏`;
}

/** Compact: 40,48 млн ֏ */
export function formatCompact(value: number | null | undefined): string {
  if (isMissing(value)) return NO_DATA;
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(2).replace('.', ',')}${NBSP}млн${NBSP}֏`;
  if (abs >= 1_000) return `${Math.round(value / 1_000)}${NBSP}тыс${NBSP}֏`;
  return `${groupDigits(value)}${NBSP}֏`;
}

/** Axis labels: 40,5M */
export function formatAxis(value: number | null | undefined): string {
  if (isMissing(value)) return NO_DATA;
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace('.', ',')}M`;
  if (abs >= 1_000) return `${Math.round(value / 1_000)}K`;
  return String(Math.round(value));
}

/** Ratio (0.253) → '25,3%' */
export function formatPercent(ratio: number | null | undefined, digits = 1): string {
  if (isMissing(ratio)) return NO_DATA;
  return `${(ratio * 100).toFixed(digits).replace('.', ',')}%`;
}

/** Signed change: '+4,2%' / '−7,8%' */
export function formatChange(ratio: number | null | undefined, digits = 1): string {
  if (isMissing(ratio)) return NO_DATA;
  const pct = ratio * 100;
  const sign = pct >= 0 ? '+' : '−';
  return `${sign}${Math.abs(pct).toFixed(digits).replace('.', ',')}%`;
}

/** Percentage points: '+1,4 п.п.' */
export function formatPoints(delta: number | null | undefined, digits = 1): string {
  if (isMissing(delta)) return NO_DATA;
  const points = delta * 100;
  const sign = points >= 0 ? '+' : '−';
  return `${sign}${Math.abs(points).toFixed(digits).replace('.', ',')}${NBSP}п.п.`;
}

/** Safe division — the calculation engine never emits #DIV/0!. */
export function safeDivide(
  numerator: number | null | undefined,
  denominator: number | null | undefined,
): number | null {
  if (isMissing(numerator) || isMissing(denominator) || denominator === 0) return null;
  const result = numerator / denominator;
  return Number.isFinite(result) ? result : null;
}

/** Month-over-month change as a ratio. */
export function momChange(
  current: number | null | undefined,
  previous: number | null | undefined,
): number | null {
  if (isMissing(current) || isMissing(previous) || previous === 0) return null;
  return (current - previous) / previous;
}

/** Decimal string from the API → number. null/undefined/'' stay null. */
export function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Project-wide rule for a headline financial figure's colour (Dashboard,
 * «Вся компания», «Расходы», ОПУ totals, YTD, table totals — anywhere a
 * final/summary number is shown):
 *
 *   negative -> always the danger tone, whatever the metric — the sign is
 *               never dropped and the number stays visually louder than a
 *               neutral one;
 *   positive -> the positive tone only when a positive number is genuinely
 *               a good result (profit, the residual) — an expense amount
 *               being positive is not "good" and stays neutral;
 *   zero     -> neutral;
 *   NULL     -> neutral ('—', never coloured as if it were zero).
 *
 * This only decides colour. What the number itself says is unchanged.
 */
export type FinancialTone = 'negative' | 'positive' | 'neutral';

export function financialTone(
  value: number | null | undefined,
  positiveIsGood: boolean,
): FinancialTone {
  if (isMissing(value)) return 'neutral';
  if (value < 0) return 'negative';
  if (value > 0 && positiveIsGood) return 'positive';
  return 'neutral';
}

export const MONTHS_RU = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
] as const;

export const MONTHS_RU_SHORT = [
  'Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн',
  'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек',
] as const;

/** 2026, 7 → 'Июль 2026' */
export function periodLabel(year: number, month: number): string {
  return `${MONTHS_RU[month - 1] ?? month} ${year}`;
}

export function periodShortLabel(month: number): string {
  return MONTHS_RU_SHORT[month - 1] ?? String(month);
}
